import type { ChatMessageDto } from "@/lib/chat";
import { extractContentText, parseMessageControls } from "@/lib/message-controls";

/**
 * チャットのメッセージ列から、ユーザーが送信したテキストを古い→新しい順で取り出す。
 * 添付などの制御行は除去し、空のテキストは履歴に含めない。
 */
export function buildUserMessageHistory(messages: readonly ChatMessageDto[]): string[] {
	const history: string[] = [];
	for (const message of messages) {
		if (message.role !== "user") continue;
		const text = parseMessageControls(extractContentText(message.content)).text.trim();
		if (text) history.push(text);
	}
	return history;
}

export type ComposerArrowAction = { kind: "history" } | { kind: "native"; edge: number };

/**
 * コンポーザーで修飾キーなしの ↑/↓ を押したときの動作を決める。
 * カーソルが先頭 (末尾) にあれば履歴へ進む。それ以外はブラウザ既定の段移動に任せ、
 * 一番上 (下) の段で動かなかったときは呼び出し側が edge へ移す。
 * 履歴を表示中は呼び出し直後でも続けて辿れるよう、1 行目 (最終行) ならカーソル位置によらず履歴へ進む。
 *
 * Args:
 *     key: 押された矢印キー。
 *     value: 入力欄のテキスト。
 *     caret: カーソル位置 (選択なし)。
 *     browsingHistory: 呼び出した履歴を未編集のまま表示しているか。
 *
 * Returns:
 *     履歴へ進むか、既定の移動で動かなかったときに移す先頭 / 末尾の位置。
 */
export function resolveComposerArrow(
	key: "ArrowUp" | "ArrowDown",
	value: string,
	caret: number,
	browsingHistory: boolean,
): ComposerArrowAction {
	const up = key === "ArrowUp";
	const edge = up ? 0 : value.length;
	const onEdgeLine = !(up ? value.slice(0, caret) : value.slice(caret)).includes("\n");
	if (caret === edge || (browsingHistory && onEdgeLine)) return { kind: "history" };
	return { kind: "native", edge };
}
